import { useCallback, useEffect, useMemo, useRef } from 'react'
import useOverlay from '@/hooks/useOverlay'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'

export type DocumentIdOverlay = ReturnType<typeof useDocumentIdOverlay>

/** An overlay that is open while a document_id is in the URL. */
export default function useDocumentIdOverlay() {
  const documentIdState = useStatefulSearchParam({ param: 'document_id' })
  const overlay = useOverlay()
  const wasDocumentIdInUrl = useRef(false)

  // The overlay and the URL can change in different renders, for example when
  // React Router renders a URL change as a transition. Follow document_id
  // entering and leaving the URL instead of comparing it with the overlay, or
  // the overlay could close or reopen in between.
  useEffect(() => {
    if (!wasDocumentIdInUrl.current && documentIdState.isInUrl) {
      overlay.open()
    } else if (wasDocumentIdInUrl.current && !documentIdState.isInUrl) {
      overlay.close()
    }
    wasDocumentIdInUrl.current = documentIdState.isInUrl
  }, [documentIdState.isInUrl, overlay])

  const { toggleValue, resetValue } = documentIdState
  const { open, close } = overlay

  const openDocument = useCallback(
    (documentId: string) => {
      toggleValue(documentId, false)
      open()
    },
    [toggleValue, open],
  )

  const closeDocument = useCallback(() => {
    close()
    resetValue(false)
  }, [close, resetValue])

  return useMemo(
    () => ({ documentIdState, overlay, openDocument, closeDocument }),
    [documentIdState, overlay, openDocument, closeDocument],
  )
}
